import { Instance, types } from 'mobx-state-tree'
import { OwnerTypes } from '@linqpal/models/src/dictionaries/UnifiedApplication'
import {
  validateDate,
  validateEmail,
  validateSsn,
  validateUSPhoneNumber,
  validateZip,
} from '@linqpal/models/src/helpers/validations'
import { MIN_COOWNER_PERCENTAGE } from '../../../ApplicationStore'
import { editableModel } from '@linqpal/models'

const CoOwnerModel = types
  .model({
    // common
    id: '',
    type: types.optional(
      types.enumeration(Object.values(OwnerTypes)),
      OwnerTypes.INDIVIDUAL,
    ),
    percentOwned: 0,
    address: '',
    city: '',
    state: '',
    zip: '',
    phone: '',
    email: '',
    // individual owner
    firstName: '',
    lastName: '',
    birthday: '',
    ssn: '',
    // entity
    entityName: '',
    ein: '',
    key: '',
  })
  .views((self) => ({
    get isFilled() {
      const requiredFields = [
        self.firstName,
        self.lastName,
        self.percentOwned,
        self.address,
        self.city,
        self.state,
        self.zip,
        self.phone,
        self.email,
      ].concat(
        self.type === OwnerTypes.INDIVIDUAL
          ? [self.birthday, self.ssn]
          : [self.entityName, self.ein],
      )

      const allRequiredFilled = requiredFields.every((value) => value)

      const allIndividualFieldsValid =
        self.type === OwnerTypes.INDIVIDUAL &&
        validateSsn(self.ssn) &&
        validateDate(self.birthday, 1900)

      const allEntityFieldsValid =
        self.type === OwnerTypes.ENTITY && validateSsn(self.ein)

      const isValidPercentage =
        MIN_COOWNER_PERCENTAGE <= self.percentOwned && self.percentOwned <= 100

      return (
        isValidPercentage &&
        allRequiredFilled &&
        validateEmail(self.email) &&
        validateUSPhoneNumber(self.phone) &&
        validateZip(self.zip) &&
        (allIndividualFieldsValid || allEntityFieldsValid)
      )
    },
  }))
  .actions((self) => ({
    // common
    setType: (
      type: typeof OwnerTypes.INDIVIDUAL | typeof OwnerTypes.ENTITY,
    ) => {
      if (type === OwnerTypes.ENTITY) {
        self.firstName = ''
        self.lastName = ''
        self.birthday = ''
        self.ssn = ''
      } else {
        self.entityName = ''
        self.ein = ''
      }
      self.type = type
    },
    setPercentage: (percentage: number) => (self.percentOwned = percentage),
    setPhone: (phone: string) => (self.phone = phone),
    setEmail: (email: string) => (self.email = email),
    // individual owner
    setFirstName: (firstName: string) => (self.firstName = firstName),
    setLastName: (lastName: string) => (self.lastName = lastName),
    setBirthday: (birthday: string) => (self.birthday = birthday),
    setSSN: (ssn: string) => (self.ssn = ssn),
    // entity owner
    setEntityName: (name: string) => (self.entityName = name),
    setEIN: (ein: string) => (self.ein = ein),

    setKey: (key: string) => (self.key = key),
  }))

export const EditableCoOwnerModel = types.compose(CoOwnerModel, editableModel())
export type CoOwner = Instance<typeof EditableCoOwnerModel>
